/* @jsxRuntime automatic */
/* @jsxImportSource @superweb/css */

import { useEffect, useId, useRef, useState } from "react";
import { createPortal } from "react-dom";

import { cssFns } from "@superweb/css";
import { useLocale } from "@superweb/intl";

import { icons } from "../icons";
import { useIsMobile } from "../mobile-context";
import { useUiColors, useUiShadows } from "../theme";
import { useTypo } from "../typo";

import { useSnackbarStackManagement } from "./snackbar-container";

const DEFAULT_TIMEOUT = 3000;

export type SnackbarProps = {
  /**
   * The text displayed in the body of snackbar.
   */
  text: string;

  /**
   * The number of milliseconds to wait before automatically calling the onClose function.
   * @defaultValue `3000`
   */
  timeout?: number;

  /**
   * Visual variant of snackbar.
   */
  view?: "critical" | "info" | "success";

  /**
   * The text is displayed on the action button. The button is displayed if the text is set.
   */
  action?: string;

  /**
   * Callback fired when mouse, touch or keyboard event occur on close button.
   */
  onClose: () => void;

  /**
   * Callback fired when mouse, touch or keyboard event occur on action button.
   */
  onAction?: () => void;
};

const SnackbarContent = ({
  view = "info",
  text,
  action,
  onClose,
  onAction,
}: SnackbarProps) => {
  const id = useId();
  const isMobile = useIsMobile();
  const locale = useLocale();
  const typo = useTypo();
  const uiColors = useUiColors();
  const uiShadows = useUiShadows();

  return (
    <div
      role="alert"
      aria-modal="false"
      aria-labelledby={id}
      css={{
        boxSizing: "border-box",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        columnGap: "16px",
        minWidth: "328px",
        maxWidth: "100%",
        ...cssFns.padding("12px", "16px"),
        color: view === "info" ? uiColors.textInvert : uiColors.everFront,
        backgroundColor: {
          critical: uiColors.statuses.danger,
          info: uiColors.backgroundInvert,
          success: uiColors.statuses.success,
        }[view],
        ...cssFns.border({
          radius: "16px",
        }),
        boxShadow: uiShadows.bottomNormal,
        pointerEvents: "auto",
      }}
    >
      <span
        css={{
          flexShrink: "0",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {view === "success" ? <icons.CheckRoundFill /> : <icons.WarningFill />}
      </span>

      <span
        id={id}
        lang={locale.language}
        css={{
          flexGrow: "1",
          ...typo({
            level: isMobile ? "caption1" : "body2",
            density: "tight",
            weight: "regular",
          }),
          hyphens: "auto",
          wordBreak: "break-word",
          ...cssFns.overflow("hidden"),
        }}
        {...(!isMobile && {
          style: {
            display: "-webkit-box",
            WebkitBoxOrient: "vertical",
            WebkitLineClamp: 2,
            textOverflow: "ellipsis",
          },
        })}
      >
        {text}
      </span>

      {action && (
        <button
          css={{
            flexShrink: "0",
            display: "flex",
            justifyContent: "center",
            ...cssFns.padding("8px", "14px"),
            ...cssFns.margin("0"),
            color: "inherit",
            backgroundColor: "transparent",
            ...cssFns.border({
              width: "0.5px",
              style: "solid",
              color: view === "info" ? uiColors.textInvert : uiColors.everFront,
              radius: "10px",
            }),
            cursor: "pointer",
          }}
          onClick={onAction}
        >
          {action}
        </button>
      )}

      <button
        css={{
          flexShrink: "0",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          ...cssFns.padding("0"),
          ...cssFns.margin("0"),
          color: "inherit",
          backgroundColor: "transparent",
          ...cssFns.border({
            width: "0",
          }),
          cursor: "pointer",
        }}
        onClick={onClose}
      >
        <icons.CrossL />
      </button>
    </div>
  );
};

const useSnackbarTimer = ({
  isPaused,
  delay,
  onTimerEnd,
}: {
  isPaused: boolean;
  delay: number;
  onTimerEnd: () => void;
}) => {
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [prevDelay, setPrevDelay] = useState(delay);
  const [remainingTime, setRemainingTime] = useState(delay);

  if (delay !== prevDelay) {
    setPrevDelay(delay);
    setRemainingTime(delay);
  }

  useEffect(() => {
    if (!isPaused && remainingTime > 0) {
      const start = Date.now();

      timerRef.current = setTimeout(() => onTimerEnd(), remainingTime);

      return () => {
        if (timerRef.current) {
          clearTimeout(timerRef.current);
          const elapsedTime = Date.now() - start;

          setRemainingTime((prevRemainingTime) =>
            prevRemainingTime - elapsedTime < 1000
              ? 1000
              : prevRemainingTime - elapsedTime,
          );
        }
      };
    }

    return;
  }, [isPaused, remainingTime, onTimerEnd]);
};

export const Snackbar = (props: SnackbarProps) => {
  const { timeout = DEFAULT_TIMEOUT, onClose } = props;

  const id = useId();
  const ref = useRef(null);

  const { container, isExpanded, styles } = useSnackbarStackManagement({
    id,
    ref,
  });

  useSnackbarTimer({
    isPaused: isExpanded,
    delay: timeout,
    onTimerEnd: onClose,
  });

  return container
    ? createPortal(
        <div ref={ref} css={styles}>
          <SnackbarContent {...props} />
        </div>,
        container,
      )
    : null;
};
